import {Injectable} from "@angular/core";
import {BehaviorSubject, firstValueFrom, Observable} from "rxjs";
import {Email} from "../model/email.model";
import {HttpClient, HttpParams} from "@angular/common/http";
import {EmailDto, PaginatedEmailsResponseDto} from "admin-api-dto";
import {environment} from "../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class EmailService {
  get emails$(): Observable<Email[]> {
    return this._emails$.asObservable();
  }

  get loading$(): Observable<boolean> {
    return this._loading$.asObservable();
  }

  get hasNextPage$(): Observable<boolean> {
    return this._hasNextPage$.asObservable();
  }

  get hasPreviousPage$(): Observable<boolean> {
    return this._hasPreviousPage$.asObservable();
  }

  private readonly _emails$ = new BehaviorSubject<Email[]>([]);
  private readonly _loading$ = new BehaviorSubject<boolean>(false);
  private readonly _hasNextPage$ = new BehaviorSubject<boolean>(false);
  private readonly _hasPreviousPage$ = new BehaviorSubject<boolean>(false);

  private readonly cursorHistory: (string | undefined)[] = [undefined];
  private currentPageIndex = 0;

  constructor(private readonly http: HttpClient) {
  }

  initialize(): void {
    if (this._emails$.value.length > 0) return;
    this.fetchPage();
  }

  async nextPage(): Promise<void> {
    if (!this._hasNextPage$.value) return;
    this.currentPageIndex++;
    await this.fetchPage();
  }

  async previousPage(): Promise<void> {
    if (!this._hasPreviousPage$.value) return;
    this.currentPageIndex--;
    await this.fetchPage();
  }

  private async fetchPage(): Promise<void> {
    this._loading$.next(true);
    try {
      const cursor = this.cursorHistory[this.currentPageIndex];
      let params = new HttpParams();
      if (cursor) {
        params = params.set('cursor', cursor);
      }
      const page = await firstValueFrom(
        this.http.get<PaginatedEmailsResponseDto>(`${environment.apiUrl}/api/private/emails`, {params})
      );

      const emails = page.items.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
      this._emails$.next(emails);

      if (page.cursor && this.currentPageIndex + 1 >= this.cursorHistory.length) {
        this.cursorHistory.push(page.cursor);
      }

      this._hasNextPage$.next(!!page.cursor);
      this._hasPreviousPage$.next(this.currentPageIndex > 0);
    } finally {
      this._loading$.next(false);
    }
  }

  getEmail(s3Key: string): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(`${environment.apiUrl}/api/private/emails/download`, {
      params: {s3Key: s3Key}
    });
  }

  openEmail(s3Key: string) {
    this.getEmail(s3Key).subscribe({
      next: ({url}) => {
        window.open(url, '_blank');
      },
      error: (error) => {
        console.error('Error opening email:', error);
      }
    });
  }

  retryEmail(s3Key: string, recipient: string): Observable<Email> {
    return this.http.put<EmailDto>(`${environment.apiUrl}/api/private/emails/retry`, {s3Key, recipient}).pipe();
  }

  getEmailsSince(since: string): Observable<EmailDto[]> {
    return this.http.get<EmailDto[]>(`${environment.apiUrl}/api/private/emails/since`, {
      params: {since}
    });
  }
}
